import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  FileXls, 
  FilePdf, 
  MagnifyingGlass, 
  Receipt, 
  CurrencyDollar,
  Calendar,
  Tag,
  CreditCard,
  Funnel,
  X,
  ChartBar,
  CheckCircle,
  Clock,
  Warning
} from "@phosphor-icons/react";

export default function ReportsPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get("/categories");
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Summary statistics
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalPayments: 0,
    totalPaid: 0,
    totalPending: 0,
    totalOverdue: 0,
    countPaid: 0,
    countPending: 0,
    countOverdue: 0,
    byCategory: {},
    byPaymentMethod: {}
  });

  const statusOptions = [
    { value: "all", label: "Todas", icon: Receipt, color: "bg-slate-500" },
    { value: "paid", label: "Pagas", icon: CheckCircle, color: "bg-green-500" },
    { value: "pending", label: "A Pagar", icon: Clock, color: "bg-blue-500" },
    { value: "overdue", label: "Vencidas", icon: Warning, color: "bg-red-500" }
  ];

  const fetchPayments = async () => {
    if (!startDate || !endDate) {
      toast.error("Informe a data inicial e final");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Data inicial deve ser menor que a data final");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    
    try {
      const response = await api.get("/reports/payments", {
        params: { start_date: startDate, end_date: endDate, status: statusFilter, category_id: categoryFilter }
      });
      
      setPayments(response.data.payments);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error("Erro ao carregar relatório");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when status or category filter changes (only if already searched)
  useEffect(() => {
    if (hasSearched && startDate && endDate) {
      fetchPayments();
    }
  }, [statusFilter, categoryFilter]);

  const handleExport = async (type) => {
    if (!startDate || !endDate) {
      toast.error("Informe a data inicial e final");
      return;
    }

    setExporting(true);
    try {
      const response = await api.get(`/reports/export/${type}`, {
        params: { start_date: startDate, end_date: endDate, status: statusFilter, category_id: categoryFilter },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: type === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = type === 'excel' ? 'xlsx' : 'pdf';
      link.download = `relatorio_parcelas_${statusFilter}_${startDate}_${endDate}.${extension}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success(`Relatório ${extension.toUpperCase()} baixado!`);
    } catch (error) {
      toast.error("Erro ao exportar relatório. Verifique se há dados no período.");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setStatusFilter("all");
    setCategoryFilter("all");
    setPayments([]);
    setHasSearched(false);
    setSummary({
      totalAmount: 0,
      totalPayments: 0,
      totalPaid: 0,
      totalPending: 0,
      totalOverdue: 0,
      countPaid: 0,
      countPending: 0,
      countOverdue: 0,
      byCategory: {},
      byPaymentMethod: {}
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Parse date string as local date (not UTC) to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Relatórios</h1>
          <p className="text-muted-foreground mt-1">
            Visualize e exporte suas parcelas por período e status
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => handleExport('excel')}
            disabled={exporting || payments.length === 0}
            className="gap-2"
            data-testid="export-excel-btn"
          >
            <FileXls size={18} />
            Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handleExport('pdf')}
            disabled={exporting || payments.length === 0}
            className="gap-2"
            data-testid="export-pdf-btn"
          >
            <FilePdf size={18} />
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Date and Search Row */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Data Inicial</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
              />
            </div>
            
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Data Final</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-input"
              />
            </div>
            
            <Button 
              onClick={fetchPayments}
              disabled={loading}
              className="gap-2"
              data-testid="search-btn"
            >
              <MagnifyingGlass size={18} />
              {loading ? "Buscando..." : "Buscar"}
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={clearFilters}
              className="text-muted-foreground"
              data-testid="clear-filters-btn"
            >
              <X size={16} className="mr-1" />
              Limpar
            </Button>
          </div>
          
          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <Label className="text-xs text-muted-foreground w-full mb-1">Filtrar por Status:</Label>
            {statusOptions.map((option) => {
              const Icon = option.icon;
              const isActive = statusFilter === option.value;
              return (
                <Button
                  key={option.value}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(option.value)}
                  className={`gap-2 ${isActive ? '' : 'hover:bg-muted'}`}
                  data-testid={`status-filter-${option.value}`}
                >
                  <Icon size={16} weight={isActive ? "fill" : "regular"} />
                  {option.label}
                </Button>
              );
            })}
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-64">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Filtrar por Categoria:</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Categorias</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: cat.color || '#8A8A8A' }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {hasSearched && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className={statusFilter === "all" ? "ring-2 ring-primary" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                  <Receipt size={20} className="text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Geral</p>
                  <p className="font-mono font-bold text-lg">
                    {formatCurrency(summary.totalPaid + summary.totalPending + summary.totalOverdue)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.countPaid + summary.countPending + summary.countOverdue} parcela(s)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={statusFilter === "paid" ? "ring-2 ring-green-500" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle size={20} className="text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pagas</p>
                  <p className="font-mono font-bold text-lg text-green-600">
                    {formatCurrency(summary.totalPaid)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.countPaid} parcela(s)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={statusFilter === "pending" ? "ring-2 ring-blue-500" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Clock size={20} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">A Pagar</p>
                  <p className="font-mono font-bold text-lg text-blue-600">
                    {formatCurrency(summary.totalPending)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.countPending} parcela(s)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={statusFilter === "overdue" ? "ring-2 ring-red-500" : ""}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <Warning size={20} className="text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Vencidas</p>
                  <p className="font-mono font-bold text-lg text-red-600">
                    {formatCurrency(summary.totalOverdue)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.countOverdue} parcela(s)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Summary by Category and Payment Method */}
      {hasSearched && payments.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* By Category */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Tag size={18} />
                Por Categoria
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {Object.entries(summary.byCategory).map(([category, data]) => (
                  <div key={category} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: data.color || '#8A8A8A' }}
                      />
                      <span className="text-sm font-medium">{category}</span>
                      <Badge variant="secondary" className="text-xs">
                        {data.count} parcela(s)
                      </Badge>
                    </div>
                    <span className="font-mono text-sm font-medium">
                      {formatCurrency(data.total)}
                    </span>
                  </div>
                ))}
                {Object.keys(summary.byCategory).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhuma categoria encontrada
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* By Payment Method */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <CreditCard size={18} />
                Por Forma de Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {Object.entries(summary.byPaymentMethod).map(([method, data]) => (
                  <div key={method} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{method}</span>
                      <Badge variant="secondary" className="text-xs">
                        {data.count} parcela(s)
                      </Badge>
                    </div>
                    <span className="font-mono text-sm font-medium">
                      {formatCurrency(data.total)}
                    </span>
                  </div>
                ))}
                {Object.keys(summary.byPaymentMethod).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhuma forma de pagamento encontrada
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payments Table */}
      {hasSearched && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ChartBar size={18} />
              Detalhes das Parcelas
              {payments.length > 0 && (
                <Badge variant="outline" className="ml-2">
                  {payments.length} registro(s)
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-pulse text-muted-foreground">Carregando...</div>
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <Receipt size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {hasSearched 
                    ? "Nenhuma parcela encontrada no período selecionado" 
                    : "Selecione um período para visualizar as parcelas"}
                </p>
              </div>
            ) : (
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Descrição</TableHead>
                      <TableHead className="font-semibold">Parcela</TableHead>
                      <TableHead className="font-semibold">Valor</TableHead>
                      <TableHead className="font-semibold">Vencimento</TableHead>
                      <TableHead className="font-semibold">Data Final</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Categoria</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment, idx) => (
                      <TableRow 
                        key={`${payment.bill_id}-${payment.installment_number}`}
                        className="hover:bg-muted/30"
                        data-testid={`payment-row-${idx}`}
                      >
                        <TableCell className="font-medium">
                          {payment.description}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {payment.installment_number}/{payment.total_installments}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">
                          {formatCurrency(payment.value)}
                        </TableCell>
                        <TableCell>
                          {formatDate(payment.due_date)}
                        </TableCell>
                        <TableCell>
                          {payment.last_due_date ? formatDate(payment.last_due_date) : "-"}
                        </TableCell>
                        <TableCell>
                          {payment.status === "paid" && (
                            <Badge className="bg-green-500/10 text-green-700 hover:bg-green-500/20">
                              <CheckCircle size={14} className="mr-1" weight="fill" />
                              Paga
                            </Badge>
                          )}
                          {payment.status === "pending" && (
                            <Badge 
                              className="bg-blue-500/10 text-blue-700 hover:bg-blue-500/20 cursor-pointer"
                              onClick={() => navigate(`/bills?filter=pending&highlight=${payment.bill_id}`)}
                              data-testid={`pay-badge-${idx}`}
                            >
                              <Clock size={14} className="mr-1" />
                              A Pagar
                            </Badge>
                          )}
                          {payment.status === "overdue" && (
                            <Badge 
                              className="bg-red-500/10 text-red-700 hover:bg-red-500/20 cursor-pointer"
                              onClick={() => navigate(`/bills?filter=overdue&highlight=${payment.bill_id}`)}
                              data-testid={`overdue-badge-${idx}`}
                            >
                              <Warning size={14} className="mr-1" weight="fill" />
                              Vencida
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {payment.category_name ? (
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: payment.category_color || '#8A8A8A' }}
                              />
                              <span>{payment.category_name}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial State */}
      {!hasSearched && (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              Selecione um período para visualizar o relatório de parcelas
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
